import React from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import useTeamMembers from '../hooks/useTeamMembers';
import type { RecordVisibility } from '../types';

interface Props {
  visibility: RecordVisibility;
  sharedWithUserIds: string[];
  onChange: (visibility: RecordVisibility, sharedWithUserIds: string[]) => void;
}

/**
 * Gedeeld door ContentTab en NotesTab: privé is de standaard, delen met een
 * specifieke persoon is altijd een bewuste actie (docs/sprekers-Module.md
 * sectie 12.4) -- nooit automatisch zichtbaar voor de hele organisatie.
 */
export default function SharingControl({ visibility, sharedWithUserIds, onChange }: Props) {
  const teamMembers = useTeamMembers();
  const isShared = visibility === 'gedeeld';

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-center gap-2">
        <Switch
          id="sharing-toggle"
          checked={isShared}
          onCheckedChange={(checked: boolean) => onChange(checked ? 'gedeeld' : 'prive', sharedWithUserIds)}
        />
        <Label htmlFor="sharing-toggle" className="font-normal">
          Delen met specifieke personen -- anders alleen voor mijzelf zichtbaar
        </Label>
      </div>
      {isShared && (
        <div className="mt-3 max-h-32 space-y-2 overflow-y-auto border-t border-slate-100 pt-3">
          {teamMembers.map((member) => (
            <label key={member.id} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={sharedWithUserIds.includes(member.id)}
                onCheckedChange={(checked: boolean) =>
                  onChange(
                    'gedeeld',
                    checked ? [...sharedWithUserIds, member.id] : sharedWithUserIds.filter((id) => id !== member.id),
                  )
                }
              />
              {member.name}
            </label>
          ))}
          {teamMembers.length === 0 && <p className="text-sm text-slate-400">Geen teamleden gevonden.</p>}
        </div>
      )}
    </div>
  );
}
